import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Typography, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import Toolbar from '@material-ui/core/Toolbar';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { shapeFields } from './shapeFields';
import { getColorForShape } from './helpers';
import { getManualShapesGeoTypesForZoomLevel } from 'api/rex/spatial';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import Stack from 'components/layout/Stack';
import PalettePicker from './PalettePicker';
import { formatValue } from 'utilities/format';
import {
  setShapesColoringType,
  setShapesGeoType,
  setColorPaletteId,
} from 'store/actions';

function MapOverlayOptions({
  classes,
  mapReady,
  shapesData,
  shapesColoringType,
  shapesGeoType,
  dropDownGeoType,
  setShapesColoringType,
  setShapesGeoType,
  zoom,
  colorPaletteId,
  colorPalettes,
  setColorPaletteId,
}) {
  // == Constants ==========================================================

  const shapeColorPalette = colorPalettes.find(
    (palette) => palette.id === colorPaletteId
  );

  // == Hooks ==============================================================

  const [selectedColoringType, setSelectedColoringType] =
    useState(shapesColoringType);
  const [colorMatrix, setColorMatrix] = useState({});
  const [coloringTypeTooltipOpen, setColoringTypeTooltipOpen] = useState(false);
  const [geoTypeTooltipOpen, setGeoTypeTooltipOpen] = useState(false);

  useEffect(() => {
    updateColorMatrix(shapesColoringType);
  }, [shapesData, colorPaletteId]);

  // == Event Handlers =====================================================

  function onColoringTypeChange(event, value) {
    setSelectedColoringType(value.props.value);
    setShapesColoringType(value.props.value);
    updateColorMatrix(value.props.value);
  }

  function onGeoTypeChange(event, value) {
    const dropDownGeoType =
      value.props.value === 'none' ? null : value.props.value;
    setShapesGeoType(dropDownGeoType);
  }

  function updateColorMatrix(coloringType) {
    const fieldDataType = shapeFields.find(
      (f) => f.dataField === coloringType
    )?.dataType;

    // Find a nonnull value to use to guarantee
    // that getColorForShape returns value colors
    const shapeWithValue = shapesData.find(
      (shape) => shape[coloringType] != null
    );

    //ok to pass any single shape as placeholder
    const colorMatrix = getColorForShape(
      shapeColorPalette,
      shapeWithValue,
      shapesData,
      coloringType,
      fieldDataType
    );

    colorMatrix.minValueDisplay = formatColorMatrixValue(
      colorMatrix.minValue,
      coloringType
    );

    colorMatrix.midValueDisplay = formatColorMatrixValue(
      colorMatrix.midValue,
      coloringType
    );

    colorMatrix.maxValueDisplay = formatColorMatrixValue(
      colorMatrix.maxValue,
      coloringType
    );

    setColorMatrix(colorMatrix);
  }

  function formatColorMatrixValue(value, coloringType) {
    var fieldSettings = shapeFields.find((f) => f.dataField === coloringType);
    if (value && fieldSettings && fieldSettings.format) {
      return formatValue(value, fieldSettings.format, false, '-n/a-');
    }
    return '-n/a-';
  }

  function getField() {
    return shapeFields.find((f) => f.dataField === selectedColoringType);
  }

  const getGeoTypes = useCallback(() => {
    return getManualShapesGeoTypesForZoomLevel(zoom);
  }, [zoom]);

  // == Component ====================================================

  if (!mapReady) {
    return null;
  }

  return (
    <Paper>
      <Toolbar variant={'dense'}>
        <Stack horizontal grow>
          <Typography variant='subtitle2'>Shapes</Typography>
        </Stack>
        <Tooltip title={'Shapes'} placement='bottom' open={geoTypeTooltipOpen}>
          <Select
            disableUnderline
            value={shapesGeoType ?? 'none'}
            onChange={onGeoTypeChange}
            displayEmpty
            name='dropDownGeoType'
            className={classes.coloringTypeDropDown}
            classes={{
              select: classes.select,
            }}
            onMouseEnter={() => setGeoTypeTooltipOpen(true)}
            onMouseLeave={() => setGeoTypeTooltipOpen(false)}
            onOpen={() => setGeoTypeTooltipOpen(false)}
          >
            <MenuItem
              className={classes.coloringTypeDropDownItem}
              value='none'
              variant='caption'
            >
              None
            </MenuItem>
            <MenuItem
              className={classes.coloringTypeDropDownItem}
              value='auto'
              variant='caption'
            >
              {shapesGeoType === 'auto' && dropDownGeoType
                ? `Auto - ${dropDownGeoType}`
                : 'Auto'}
            </MenuItem>
            {getGeoTypes().map((type) => {
              return (
                <MenuItem
                  key={type.value}
                  variant='caption'
                  className={classes.coloringTypeDropDownItem}
                  value={type.value}
                  disabled={!type.enabled}
                >
                  {type.caption}
                </MenuItem>
              );
            })}
          </Select>
        </Tooltip>
        {dropDownGeoType && (
          <Tooltip
            title={getField().caption}
            placement='bottom'
            open={coloringTypeTooltipOpen}
          >
            <Select
              disableUnderline
              value={selectedColoringType}
              onChange={onColoringTypeChange}
              displayEmpty
              name='shapeColoring'
              className={classes.coloringTypeDropDown}
              classes={{
                select: classes.select,
              }}
              onMouseEnter={() => setColoringTypeTooltipOpen(true)}
              onMouseLeave={() => setColoringTypeTooltipOpen(false)}
              onOpen={() => setColoringTypeTooltipOpen(false)}
            >
              {shapeFields.map((field) => {
                return (
                  <MenuItem
                    key={field.dataField}
                    variant='caption'
                    className={classes.coloringTypeDropDownItem}
                    value={field.dataField}
                  >
                    {field.caption}
                  </MenuItem>
                );
              })}
            </Select>
          </Tooltip>
        )}
        {dropDownGeoType &&
          selectedColoringType &&
          selectedColoringType !== '' &&
          getField().dataType === 'number' && (
            <div
              style={{
                marginLeft: 10,
                borderRadius: 3,
                minWidth: 130,
                display: 'flex',
                justifyContent: 'space-between',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  width: '50%',
                  height: '100%',
                  background: `linear-gradient(to right, ${
                    colorMatrix.minValueColor ?? colorMatrix.valueColor
                  }, ${colorMatrix.midValueColor ?? colorMatrix.valueColor})`,
                }}
              ></div>
              <div
                style={{
                  position: 'absolute',
                  width: '50%',
                  height: '100%',
                  background: `linear-gradient(to right, ${
                    colorMatrix.midValueColor ?? colorMatrix.valueColor
                  }, ${colorMatrix.maxValueColor ?? colorMatrix.valueColor})`,
                  right: 0,
                }}
              ></div>
              <Typography
                variant='caption'
                className={classes.coloringLegendItemText}
              >
                {colorMatrix.minValueDisplay}
              </Typography>
              <Typography
                variant='caption'
                className={classes.coloringLegendItemText}
              >
                {colorMatrix.midValueDisplay}
              </Typography>
              <Typography
                variant='caption'
                className={classes.coloringLegendItemText}
              >
                {colorMatrix.maxValueDisplay}
              </Typography>
            </div>
          )}
        {dropDownGeoType && (
          <PalettePicker
            palettes={colorPalettes}
            selectedPaletteId={colorPaletteId}
            onPaletteChange={setColorPaletteId}
          />
        )}
      </Toolbar>
    </Paper>
  );
}

// == Styles ===========================

const styles = {
  coloringTypeDropDown: {
    marginLeft: 10,
    marginTop: 0,
    fontSize: 12,
    padding: 0,
    margin: 0,
  },
  coloringTypeDropDownItem: {
    fontSize: 12,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 0,
    paddingBottom: 0,
  },
  coloringLegendItemText: {
    marginLeft: 5,
    marginRight: 5,
    marginTop: 3,
    marginBottom: 3,
    color: '#ffffff',
    zIndex: 1,
  },
  select: {
    paddingTop: 9,
  },
  invertToggleContainer: {
    marginLeft: -1,
    marginTop: -8,
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    '&:focus': { outline: 'none' },
  },
};

// == Connect ===================================

function mapState({ assumptionMapState }) {
  return {
    shapesData: assumptionMapState.shapesData,
    shapesColoringType: assumptionMapState.shapesColoringType,
    shapesGeoType: assumptionMapState.shapesGeoType,
    dropDownGeoType: assumptionMapState.dropDownGeoType,
    mapReady:
      assumptionMapState.bounds !== null &&
      assumptionMapState.bounds !== undefined,
    zoom: assumptionMapState.zoom,
    colorPaletteId: assumptionMapState.colorPaletteId,
    colorPalettes: assumptionMapState.colorPalettes,
  };
}

const mapDispatch = {
  setShapesColoringType,
  setShapesGeoType,
  setColorPaletteId,
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(MapOverlayOptions);
